import { COUNTRIES } from "../components/modules/Dispatch/RouteExecution/constants";

const PUERTO_RICO_STATE = "PR";

/** Top-level keys in `internationalDocuments` config (nested `key` is ignored). */
export const INTERNATIONAL_DOCUMENT_COUNTRY_KEYS = {
  CANADA: "CANADA",
  MEXICO: "MEXICO",
  PUERTO_RICO: "PUERTORICO",
};

const KNOWN_CONFIG_KEYS = Object.values(INTERNATIONAL_DOCUMENT_COUNTRY_KEYS);

export const INTERNATIONAL_DOCUMENTATION_FIELDS = {
  [INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.CANADA]: {
    fieldName: "receivedCanadianDocumentation",
  },
  [INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.MEXICO]: {
    fieldName: "receivedMexicoDocumentation",
  },
  [INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.PUERTO_RICO]: {
    fieldName: "receivedPuertoRicoDocumentation",
  },
};

const normalize = (value) => value?.trim().toUpperCase() ?? "";

const getEnabledConfigKey = (config, configKey) => {
  if (config[configKey]?.enabled) {
    return configKey;
  }
  const matchedKey = Object.keys(config).find(
    (key) => key.toUpperCase() === configKey.toUpperCase() && config[key]?.enabled
  );
  return matchedKey ?? null;
};

/** Web uses only `name` and `enabled` per section; `documents` is ignored. */
export const parseInternationalDocumentsConfig = (rawConfig) => {
  if (!rawConfig || typeof rawConfig !== "string") {
    return null;
  }
  try {
    const parsed = JSON.parse(rawConfig);
    const config = {};
    for (const key of Object.keys(parsed)) {
      const canonicalKey = KNOWN_CONFIG_KEYS.find(
        (known) => known === normalize(key)
      );
      if (!canonicalKey) {
        continue;
      }
      const section = parsed[key];
      if (!section || typeof section !== "object") {
        continue;
      }
      config[canonicalKey] = {
        name: section.name ?? canonicalKey,
        enabled: Boolean(section.enabled),
      };
    }
    return Object.keys(config).length ? config : null;
  } catch (_error) {
    return null;
  }
};

const resolveSectionKey = (countryCode, config) => {
  const normalizedCountry = normalize(countryCode);
  if (!normalizedCountry) {
    return null;
  }
  if (normalizedCountry === COUNTRIES.CN) {
    return getEnabledConfigKey(config, INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.CANADA);
  }
  if (normalizedCountry === COUNTRIES.MX) {
    return getEnabledConfigKey(config, INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.MEXICO);
  }
  if (normalizedCountry === PUERTO_RICO_STATE) {
    return getEnabledConfigKey(
      config,
      INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.PUERTO_RICO
    );
  }
  return null;
};

const partySectionKeys = (countryCode, consigneeState, locationCities, config) => {
  const country = normalize(countryCode);
  if (!country) {
    return [];
  }
  if (country === COUNTRIES.US) {
    const state = normalize(consigneeState);
    if (state === PUERTO_RICO_STATE) {
      const prKey = getEnabledConfigKey(
        config,
        INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.PUERTO_RICO
      );
      return prKey ? [prKey] : [];
    }
    const hasPuertoRicoCity = (locationCities ?? []).some(
      (city) => normalize(city.state) === PUERTO_RICO_STATE
    );
    if (hasPuertoRicoCity) {
      const prKey = getEnabledConfigKey(
        config,
        INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.PUERTO_RICO
      );
      return prKey ? [prKey] : [];
    }
    return [];
  }
  const sectionKey = resolveSectionKey(countryCode, config);
  return sectionKey ? [sectionKey] : [];
};

/**
 * Which documentation toggles to show (DriverApp section rules; web = one switch per country).
 */
export const getVisibleInternationalDocumentationSections = ({
  config,
  shipperCountry,
  consigneeCountry,
  shipperState,
  consigneeState,
  locationCities,
}) => {
  if (!config) {
    return [];
  }

  const shipper = normalize(shipperCountry);
  const consignee = normalize(consigneeCountry);
  if (!shipper || !consignee) {
    return [];
  }

  const shipperKeys = partySectionKeys(
    shipperCountry,
    shipperState,
    [],
    config
  );
  const consigneeKeys = partySectionKeys(
    consigneeCountry,
    consigneeState,
    locationCities,
    config
  );

  if (shipper === consignee) {
    if (shipper !== COUNTRIES.US) {
      return [];
    }
    const shipperIsPuertoRico = shipperKeys.length > 0;
    const consigneeIsPuertoRico = consigneeKeys.length > 0;
    if (shipperIsPuertoRico === consigneeIsPuertoRico) {
      return [];
    }
    const puertoRicoSectionKey = shipperKeys[0] ?? consigneeKeys[0];
    return puertoRicoSectionKey ? [puertoRicoSectionKey] : [];
  }

  const seen = new Set();
  const sections = [];
  for (const key of [...shipperKeys, ...consigneeKeys]) {
    if (!seen.has(key)) {
      seen.add(key);
      sections.push(key);
    }
  }
  return sections;
};

const DOCUMENTATION_TOGGLE_LABELS = {
  [INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.CANADA]: "Received Canadian Documentation",
  [INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.MEXICO]: "Received Mexico Documentation",
  [INTERNATIONAL_DOCUMENT_COUNTRY_KEYS.PUERTO_RICO]:
    "Received Puerto Rico Documentation",
};

export const getInternationalDocumentationToggleLabel = (sectionKey) =>
  DOCUMENTATION_TOGGLE_LABELS[sectionKey] ??
  "Received International Documentation";
